// src/stores/CertificateStore.ts - Fixed Version
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { RootStore } from './index';
import certificateService from '@/services/certificateService';
import {
    Certificate,
    CertificateApplication,
    CertificateTemplate,
    CertificateVerification,
    CertificateType,
    ApplicationType,
    ApplicationStatus,
    CertificateStatus,
    Priority,
    LicenseStats,
    ApplicationData,
    SupportedAssociation,
    CertificateSearchFilters,
    ApplicationSearchFilters,
    ValidationResult,
    APIResponse
} from '@/types/certificate';
import { generateSlug } from '@/utils/string-utils';

export interface CertificateApplicationForm {
    contentId: number | '';
    certificateType: CertificateType | '';
    applicationType: ApplicationType;
    priority: Priority;
    applicationData: ApplicationData;
    supportingDocuments: File[];
    applicantDetails?: any;
}

class CertificateStore {
    rootStore: RootStore;

    // ✅ Add certificateService reference
    certificateService = certificateService;

    // State cho applications
    applications: CertificateApplication[] = [];
    pendingApplications: CertificateApplication[] = [];
    currentApplication: CertificateApplication | null = null;

    // State cho certificates
    certificates: Certificate[] = [];
    currentCertificate: Certificate | null = null;

    // State cho templates
    templates: CertificateTemplate[] = [];
    currentTemplate: CertificateTemplate | null = null;

    // State cho statistics
    stats: LicenseStats | null = null;

    // State cho verification
    verificationResult: {
        certificate: Certificate | null;
        verification: CertificateVerification | null;
    } | null = null;

    // State cho form
    applicationForm: CertificateApplicationForm = {
        contentId: '',
        certificateType: '',
        applicationType: 'new',
        priority: 'normal',
        applicationData: {
            workTitle: '',
            workType: '',
            creators: [],
            description: '',
            firstPublicationDate: '',
            registrationTerritory: [],
            previousRegistrations: []
        },
        supportingDocuments: []
    };

    // State cho integrations
    supportedAssociations: SupportedAssociation[] = [
        {
            id: 'vcpmc',
            name: 'Trung tâm Bảo vệ Quyền tác giả Âm nhạc Việt Nam (VCPMC)',
            type: 'music',
            description: 'Tổ chức thu phí và phân phối tiền bản quyền cho các tác giả, nhà xuất bản âm nhạc',
            isActive: true,
            supportedcontentKinds: ['music', 'audio'],
            processingFees: {
                new: 500000,
                renewal: 300000,
                transfer: 200000,
                amendment: 150000
            },
            estimatedProcessingTime: { normal: 15, urgent: 7, express: 3 }
        },
        {
            id: 'vpa',
            name: 'Hiệp hội Nhiếp ảnh Việt Nam (VPA)',
            type: 'photography',
            description: 'Tổ chức bảo vệ quyền tác giả nhiếp ảnh',
            isActive: true,
            supportedcontentKinds: ['image'],
            processingFees: {
                new: 300000,
                renewal: 200000,
                transfer: 150000,
                amendment: 100000
            },
            estimatedProcessingTime: { normal: 10, urgent: 5, express: 2 }
        },
        {
            id: 'vwa',
            name: 'Hội Nhà văn Việt Nam (VWA)',
            type: 'literature',
            description: 'Tổ chức bảo vệ quyền tác giả văn học',
            isActive: true,
            supportedcontentKinds: ['text'],
            processingFees: {
                new: 400000,
                renewal: 250000,
                transfer: 180000,
                amendment: 120000
            },
            estimatedProcessingTime: { normal: 12, urgent: 6, express: 3 }
        },
        {
            id: 'vfda',
            name: 'Hiệp hội Điện ảnh Việt Nam (VFDA)',
            type: 'film',
            description: 'Tổ chức quản lý bản quyền phim ảnh',
            isActive: true,
            supportedcontentKinds: ['video'],
            processingFees: {
                new: 800000,
                renewal: 500000,
                transfer: 350000,
                amendment: 200000
            },
            estimatedProcessingTime: { normal: 20, urgent: 10, express: 5 }
        }
    ];

    // Search filters
    applicationFilters: ApplicationSearchFilters = {};
    certificateFilters: CertificateSearchFilters = {};

    // Loading states
    isLoading: boolean = false;
    isLoadingApplications: boolean = false;
    isLoadingCertificates: boolean = false;
    isSubmitting: boolean = false;
    isVerifying: boolean = false;

    // Other states
    error: string | null = null;
    applicationSubmitted: boolean = false;
    verificationCompleted: boolean = false;

    // ✅ Add pagination property
    pagination: any = null;

    // Pagination
    applicationPage: number = 1;
    applicationPageSize: number = 10;
    applicationTotal: number = 0;

    certificatePage: number = 1;
    certificatePageSize: number = 10;
    certificateTotal: number = 0;

    activeTab: 'all' | CertificateType = 'all';
    searchTimeout: NodeJS.Timeout | null = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this);
    }

    /**
     * ✅ Fix setError method
     */
    setError(error: string | null): void {
        runInAction(() => {
            this.error = error;
        });
    }

    /**
     * Enhanced loading state management
     */
    setLoading(loading: boolean, type?: 'applications' | 'pendingApplications' | 'certificates'): void {
        runInAction(() => {
            if (type === 'applications') {
                this.isLoadingApplications = loading;
            } else if (type === 'pendingApplications') {
                this.isLoadingApplications = loading; // Reuse same flag or add separate one
            } else if (type === 'certificates') {
                this.isLoadingCertificates = loading;
            } else {
                // Default: set all loading states
                this.isLoading = loading;
            }
        });
    }

    /**
     * Set submitting state for form actions
     */
    setSubmitting(submitting: boolean): void {
        runInAction(() => {
            this.isSubmitting = submitting;
        });
    }

    /**
     * === FORM MANAGEMENT ===
     */

    setFormField<K extends keyof CertificateApplicationForm>(
        field: K,
        value: CertificateApplicationForm[K]
    ) {
        this.applicationForm[field] = value;
    }

    setApplicationData<K extends keyof ApplicationData>(
        field: K,
        value: ApplicationData[K]
    ) {
        this.applicationForm.applicationData[field] = value;
    }

    addCreator(creator: ApplicationData['creators'][0]) {
        this.applicationForm.applicationData.creators.push(creator);
    }

    removeCreator(index: number) {
        this.applicationForm.applicationData.creators.splice(index, 1);
    }

    updateCreator(index: number, creator: ApplicationData['creators'][0]) {
        this.applicationForm.applicationData.creators[index] = creator;
    }

    addSupportingDocument(file: File) {
        this.applicationForm.supportingDocuments.push(file);
    }

    removeSupportingDocument(index: number) {
        this.applicationForm.supportingDocuments.splice(index, 1);
    }

    resetForm() {
        this.applicationForm = {
            contentId: '',
            certificateType: '',
            applicationType: 'new',
            priority: 'normal',
            applicationData: {
                workTitle: '',
                workType: '',
                creators: [],
                description: '',
                firstPublicationDate: '',
                registrationTerritory: [],
                previousRegistrations: []
            },
            supportingDocuments: []
        };
        this.applicationSubmitted = false;
        this.error = null;
    }

    /**
     * === VALIDATION ===
     */

    validateBasicInfo(): ValidationResult {
        const errors: Record<string, string> = {};
        const { applicationData } = this.applicationForm;

        if (!applicationData.workTitle.trim()) {
            errors.workTitle = 'Tên tác phẩm là bắt buộc';
        }
        if (!applicationData.workType.trim()) {
            errors.workType = 'Loại tác phẩm là bắt buộc';
        }
        if (!applicationData.description.trim()) {
            errors.description = 'Mô tả là bắt buộc';
        }
        if (!applicationData.firstPublicationDate) {
            errors.firstPublicationDate = 'Ngày công bố là bắt buộc';
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }

    validateCreators(): ValidationResult {
        const errors: Record<string, string> = {};
        const { creators } = this.applicationForm.applicationData;

        if (creators.length === 0) {
            errors.creators = 'Phải có ít nhất một tác giả/chủ sở hữu';
        }

        const totalPercentage = creators.reduce((sum, c) => sum + c.percentage, 0);
        if (totalPercentage !== 100) {
            errors.percentage = `Tổng phần trăm phải bằng 100% (hiện tại: ${totalPercentage}%)`;
        }

        creators.forEach((creator, index) => {
            if (!creator.name.trim()) {
                errors[`creator_${index}_name`] = 'Tên là bắt buộc';
            }
            if (!creator.role.trim()) {
                errors[`creator_${index}_role`] = 'Vai trò là bắt buộc';
            }
            if (creator.percentage <= 0 || creator.percentage > 100) {
                errors[`creator_${index}_percentage`] = 'Phần trăm phải từ 1-100';
            }
        });

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }

    /**
     * === APPLICATION CRUD ===
     */

    async createApplication(applicationData?: Partial<CertificateApplicationForm>) {
        this.isSubmitting = true;
        this.error = null;

        try {
            let updateUserProfile: any = {};
            if(applicationData?.applicantDetails?.type === "individual") {
                if(applicationData?.applicantDetails?.phone && (
                    !this.rootStore.authStore?.currentUser?.user_profile.phoneNumber 
                    || !this.rootStore.authStore?.currentUser?.user_profile.phoneNumber !== applicationData?.applicantDetails?.phone
                )){
                    updateUserProfile['phoneNumber'] = applicationData?.applicantDetails?.phone || '';
                };
                if(applicationData?.applicantDetails?.idNumber && (
                    !this.rootStore.authStore?.currentUser?.user_profile.citizenId 
                    || !this.rootStore.authStore?.currentUser?.user_profile.citizenId !== applicationData?.applicantDetails?.idNumber
                )){
                    updateUserProfile['citizenId'] = applicationData?.applicantDetails?.idNumber || '';
                };
                if(applicationData?.applicantDetails?.address && (
                    !this.rootStore.authStore?.currentUser?.user_profile.address 
                    || !this.rootStore.authStore?.currentUser?.user_profile.address !== applicationData?.applicantDetails?.address
                )){
                    updateUserProfile['address'] = applicationData?.applicantDetails?.address || '';
                };
            };
            
            if(!this.rootStore.authStore?.currentUser?.user_profile?.taxInformation && applicationData?.applicantDetails?.type === "organization"){
                updateUserProfile['taxInformation'] = JSON.stringify({
                    type: 'organization',
                    name: applicationData?.applicantDetails?.name || '',
                    address: applicationData?.applicantDetails?.address || '',
                    taxCode: applicationData?.applicantDetails?.taxCode || '',
                })
            }

            if(Object.keys(updateUserProfile).length > 0) {
                await this.rootStore.userStore.updateCurrentProfile(updateUserProfile);
            }

            // Merge với form data nếu có
            const data = applicationData || this.applicationForm;

            const application = await certificateService.createApplication(data);

            runInAction(() => {
                this.currentApplication = application;
                this.applications.unshift(application);
                this.applicationSubmitted = true;
                this.isSubmitting = false;
            });

            return application;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể tạo đơn đăng ký';
                this.isSubmitting = false;
            });
            throw error;
        }
    }

    async updateApplication(documentId: string, updateData: Partial<CertificateApplication>) {
        this.isSubmitting = true;
        this.error = null;

        try {
            const application = await certificateService.updateApplication(documentId, updateData);

            runInAction(() => {
                this.currentApplication = application;
                // Cập nhật trong danh sách
                const index = this.applications.findIndex(app => app.documentId === documentId);
                if (index !== -1) {
                    this.applications[index] = application;
                }
                this.isSubmitting = false;
            });

            return application;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể cập nhật đơn đăng ký';
                this.isSubmitting = false;
            });
            throw error;
        }
    }

    async deleteApplication(documentId: string) {
        this.isLoading = true;
        this.error = null;

        try {
            await certificateService.deleteApplication(documentId);

            runInAction(() => {
                this.applications = this.applications.filter(app => app.documentId !== documentId);
                if (this.currentApplication?.documentId === documentId) {
                    this.currentApplication = null;
                }
                this.isLoading = false;
            });
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể xóa đơn đăng ký';
                this.isLoading = false;
            });
            throw error;
        }
    }

    async submitApplication(documentId: string) {
        this.isSubmitting = true;
        this.error = null;

        try {
            const application = await certificateService.submitApplication(documentId);

            runInAction(() => {
                this.currentApplication = application;
                // Cập nhật trong danh sách
                const index = this.applications.findIndex(app => app.documentId === documentId);
                if (index !== -1) {
                    this.applications[index] = application;
                }
                this.isSubmitting = false;
            });

            return application;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể gửi đơn đăng ký';
                this.isSubmitting = false;
            });
            throw error;
        }
    }

    /**
     * === FETCH METHODS ===
     */

    async fetchUserApplications(params?: {
        page?: number;
        pageSize?: number;
        filters?: ApplicationSearchFilters;
    }) {
        this.isLoadingApplications = true;
        this.error = null;

        try {
            const result = await certificateService.getUserApplications({
                page: params?.page || this.applicationPage,
                pageSize: params?.pageSize || this.applicationPageSize,
                ...params?.filters
            });

            runInAction(() => {
                this.applications = result.data;
                this.applicationTotal = result.meta?.pagination?.total || 0;
                this.applicationPage = params?.page || this.applicationPage;
                this.isLoadingApplications = false;
            });

            return result;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể tải danh sách đơn đăng ký';
                this.isLoadingApplications = false;
            });
            throw error;
        }
    }

    async fetchApplicationById(id: number) {
        this.isLoading = true;
        this.error = null;

        try {
            const application = await certificateService.getApplicationById(id);

            runInAction(() => {
                this.currentApplication = application;
                this.isLoading = false;
            });

            return application;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể tải thông tin đơn đăng ký';
                this.isLoading = false;
            });
            throw error;
        }
    }

    /**
     * ===== ADMIN REVIEW METHODS =====
     */

    /**
     * Start reviewing an application (submitted → under_review)
     */
    async startReviewApplication(documentId: string): Promise<CertificateApplication> {
        this.setLoading(true);
        try {
            const result = await certificateService.startReviewApplication(documentId);

            // Update local state
            runInAction(() => {
                const index = this.pendingApplications.findIndex(app => app.documentId === documentId);
                if (index !== -1) {
                    this.pendingApplications[index] = result;
                }
            });

            console.log('✅ Application review started successfully:', result.applicationNumber);
            return result;
        } catch (error) {
            console.error('❌ Error starting application review:', error);
            this.setError('Không thể bắt đầu xem xét đơn đăng ký');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Request additional information (any status → additional_info_required)
     */
    async requestAdditionalInfo(documentId: string, message: string): Promise<CertificateApplication> {
        this.setLoading(true);
        try {
            const result = await certificateService.requestAdditionalInfo(documentId, message);

            // Update local state
            runInAction(() => {
                const index = this.pendingApplications.findIndex(app => app.documentId === documentId);
                if (index !== -1) {
                    this.pendingApplications[index] = result;
                }
            });

            console.log('✅ Additional info requested successfully:', result.applicationNumber);
            return result;
        } catch (error) {
            console.error('❌ Error requesting additional info:', error);
            this.setError('Không thể yêu cầu bổ sung thông tin');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Fetch pending applications for admin review
     */
    async fetchPendingApplications(params: {
        page?: number;
        pageSize?: number;
        certificateType?: CertificateType;
        priority?: string;
    } = {}): Promise<void> {
        this.setLoading(true, 'pendingApplications');
        try {
            const response = await certificateService.getPendingApplications(params);

            runInAction(() => {
                this.pendingApplications = response.data;
                this.pagination = response.meta?.pagination;
            });

            console.log(`📊 Loaded ${response.data.length} pending applications`);
        } catch (error) {
            console.error('❌ Error fetching pending applications:', error);
            this.setError('Không thể tải danh sách đơn chờ duyệt');
            throw error;
        } finally {
            this.setLoading(false, 'pendingApplications');
        }
    }

    /**
     * Enhanced approve application method
     */
    async approveApplication(
        documentId: string,
        data: {
            reviewNotes?: string;
            reviewedBy?: number;
            applicationData?: any;
            supportingDocuments?: number[];
            certificateFile?: number;  // ✅ SINGLE FILE, not array
        }
    ): Promise<{
        application: CertificateApplication;
        certificate?: Certificate;
    }> {
        try {
            this.isSubmitting = true;
            this.error = null;
    
            console.log('=== CERTIFICATE STORE - APPROVE APPLICATION ===');
            console.log('DocumentId:', documentId);
            console.log('Approval data:', data);
    
            // 🔥 Extract wipoNumber từ applicationData nếu có
            const wipoNumber = data.applicationData?.wipoNumber;
    
            // 🔥 Call certificateService với wipoNumber
            const result = await certificateService.approveApplication(documentId, {
                reviewNotes: data.reviewNotes || 'Đã xem xét và phê duyệt',
                reviewedBy: data.reviewedBy,
                applicationData: data.applicationData,  // ← Pass full applicationData
                supportingDocuments: data.supportingDocuments,
                certificateFile: data.certificateFile
            });
    
            console.log('✅ Approval successful:', {
                applicationId: result.application.id,
                certificateId: result.certificate?.id,
                certificateNumber: result.certificate?.certificateNumber,
                wipoNumber: result.certificate?.wipoNumber,
                hasCertificateFile: !!result.certificate?.certificateFile
            });
    
            // Update local state
            runInAction(() => {
                const index = this.pendingApplications.findIndex(
                    app => app.documentId === documentId
                );
                if (index !== -1) {
                    this.pendingApplications.splice(index, 1);
                }
    
                if (result.certificate) {
                    this.certificates.unshift(result.certificate);
                }
    
                this.isSubmitting = false;
            });
    
            return result;
    
        } catch (error: any) {
            console.error('❌ Error in approveApplication:', error);
            
            runInAction(() => {
                this.error = error.message || 'Không thể phê duyệt đơn đăng ký';
                this.isSubmitting = false;
            });
    
            throw error;
        }
    }

    /**
     * Enhanced reject application method
     */
    async rejectApplication(documentId: string, rejectionReason: string): Promise<CertificateApplication> {
        this.setSubmitting(true);
        try {
            const result = await certificateService.rejectApplication(documentId, rejectionReason);

            // Remove from pending applications
            runInAction(() => {
                this.pendingApplications = this.pendingApplications.filter(app => app.documentId !== documentId);

                // Update in regular applications
                const existingIndex = this.applications.findIndex(app => app.documentId === documentId);
                if (existingIndex !== -1) {
                    this.applications[existingIndex] = result;
                } else {
                    this.applications.unshift(result);
                }
            });

            console.log('✅ Application rejected successfully:', result.applicationNumber);
            return result;
        } catch (error) {
            console.error('❌ Error rejecting application:', error);
            this.setError('Không thể từ chối đơn đăng ký');
            throw error;
        } finally {
            this.setSubmitting(false);
        }
    }

    /**
     * === CERTIFICATES ===
     */

    async fetchUserCertificates(params?: {
        page?: number;
        pageSize?: number;
        filters?: CertificateSearchFilters;
    }) {
        this.setLoading(true, 'certificates')
        this.error = null;

        try {
            const result = await certificateService.getUserCertificates({
                page: params?.page || this.certificatePage,
                pageSize: params?.pageSize || this.certificatePageSize,
                ...params?.filters
            });

            runInAction(() => {
                this.certificates = result.data;
                this.certificateTotal = result.meta?.pagination?.total || 0;
                this.certificatePage = params?.page || this.certificatePage;
            });

            return result;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể tải danh sách giấy phép';
            });
            throw error;
        } finally {
            this.setLoading(false, 'certificates');
        }
    }

    async fetchCertificateById(id: number) {
        this.isLoading = true;
        this.error = null;

        try {
            const certificate = await certificateService.getCertificateById(id);

            runInAction(() => {
                this.currentCertificate = certificate;
                this.isLoading = false;
            });

            return certificate;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể tải thông tin giấy phép';
                this.isLoading = false;
            });
            throw error;
        }
    }

    async revokeCertificate(documentId: string, reason: string) {
        this.isLoading = true;
        this.error = null;

        try {
            const certificate = await certificateService.revokeCertificate(documentId, reason);

            runInAction(() => {
                // Cập nhật trong danh sách
                const index = this.certificates.findIndex(cert => cert.documentId === documentId);
                if (index !== -1) {
                    this.certificates[index] = certificate;
                }
                if (this.currentCertificate?.documentId === documentId) {
                    this.currentCertificate = certificate;
                }
                this.isLoading = false;
            });

            return certificate;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể thu hồi giấy phép';
                this.isLoading = false;
            });
            throw error;
        }
    }

    async renewCertificate(documentId: string) {
        this.isLoading = true;
        this.error = null;

        try {
            const application = await certificateService.renewCertificate(documentId);

            runInAction(() => {
                this.applications.unshift(application);
                this.isLoading = false;
            });

            return application;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể gia hạn giấy phép';
                this.isLoading = false;
            });
            throw error;
        }
    }

    /**
     * === VERIFICATION ===
     */

    async verifyCertificate(certificateNumber: string, includeCode?: boolean) {
        this.isVerifying = true;
        this.error = null;
        this.verificationCompleted = false;

        try {
            const result = await certificateService.verifyCertificate(certificateNumber, includeCode);

            runInAction(() => {
                this.verificationResult = result;
                this.verificationCompleted = true;
                this.isVerifying = false;
            });

            return result;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể xác thực giấy phép';
                this.isVerifying = false;
            });
            throw error;
        }
    }

    clearVerificationResult() {
        this.verificationResult = null;
        this.verificationCompleted = false;
    }

    /**
     * === TEMPLATES ===
     */

    async fetchTemplates(certificateType?: CertificateType) {
        this.isLoading = true;
        this.error = null;

        try {
            const templates = await certificateService.getTemplates(certificateType);

            runInAction(() => {
                this.templates = templates;
                this.isLoading = false;
            });

            return templates;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể tải danh sách mẫu';
                this.isLoading = false;
            });
            throw error;
        }
    }

    async fetchTemplateById(id: number) {
        this.isLoading = true;
        this.error = null;

        try {
            const template = await certificateService.getTemplateById(id);

            runInAction(() => {
                this.currentTemplate = template;
                this.isLoading = false;
            });

            return template;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể tải thông tin mẫu';
                this.isLoading = false;
            });
            throw error;
        }
    }

    /**
     * === STATISTICS ===
     */

    async fetchStatistics() {
        this.isLoading = true;
        this.error = null;

        try {
            const stats = await certificateService.getStatistics();

            runInAction(() => {
                this.stats = stats;
                this.isLoading = false;
            });

            return stats;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể tải thống kê';
                this.isLoading = false;
            });
            throw error;
        }
    }

    /**
     * === EXTERNAL INTEGRATIONS ===
     */

    async registerWithExternal(
        certificateType: CertificateType,
        documentId: string,
        externalData: any
    ) {
        this.isSubmitting = true;
        this.error = null;

        try {
            let result;

            switch (certificateType) {
                case 'wipo':
                    result = await certificateService.registerWithWIPO(documentId, externalData);
                    break;
                case 'noip':
                    result = await certificateService.registerWithNOIP(documentId, externalData);
                    break;
                case 'association':
                    result = await certificateService.registerWithAssociation(
                        documentId,
                        externalData.associationId,
                        externalData
                    );
                    break;
                default:
                    throw new Error('Unsupported external registration type');
            }

            runInAction(() => {
                this.isSubmitting = false;
            });

            return result;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể đăng ký với tổ chức bên ngoài';
                this.isSubmitting = false;
            });
            throw error;
        }
    }

    async fetchSupportedAssociations() {
        this.isLoading = true;
        this.error = null;

        try {
            const associations = await certificateService.getSupportedAssociations();

            runInAction(() => {
                this.supportedAssociations = associations;
                this.isLoading = false;
            });

            return associations;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể tải danh sách hiệp hội';
                this.isLoading = false;
            });

            // Return mock data if API fails
            return this.supportedAssociations;
        }
    }

    /**
     * === SEARCH & FILTERS ===
     */

    setApplicationFilters(filters: ApplicationSearchFilters) {
        this.applicationFilters = filters;
        this.applicationPage = 1; // Reset to first page
    }

    setCertificateFilters(filters: CertificateSearchFilters) {
        this.certificateFilters = filters;
        this.certificatePage = 1; // Reset to first page
    }

    clearFilters() {
        this.applicationFilters = {};
        this.certificateFilters = {};
    }

    /**
     * === PAGINATION ===
     */

    setApplicationPage(page: number) {
        this.applicationPage = page;
    }

    setCertificatePage(page: number) {
        this.certificatePage = page;
    }

    /**
     * === COMPUTED VALUES ===
     */

    get isFormValid() {
        const { contentId, certificateType, applicationData } = this.applicationForm;
        const { workTitle, workType, description, creators } = applicationData;

        return !!(
            contentId &&
            certificateType &&
            workTitle.trim() &&
            workType.trim() &&
            description.trim() &&
            creators.length > 0 &&
            creators.every(c => c.name.trim() && c.role.trim() && c.percentage > 0) &&
            creators.reduce((sum, c) => sum + c.percentage, 0) === 100
        );
    }

    get applicationsByStatus() {
        const byStatus: Record<ApplicationStatus, CertificateApplication[]> = {
            draft: [],
            submitted: [],
            under_review: [],
            additional_info_required: [],
            approved: [],
            rejected: [],
            withdrawn: []
        };

        this.applications.forEach(app => {
            byStatus[app.applicationStatus].push(app);
        });

        return byStatus;
    }

    get certificatesByType() {
        const byType: Record<CertificateType, Certificate[]> = {
            vietrro: [],
            wipo: [],
            noip: [],
            association: []
        };

        this.certificates.forEach(cert => {
            byType[cert.certificateType].push(cert);
        });

        return byType;
    }

    get certificatesByStatus() {
        const byStatus: Record<CertificateStatus, Certificate[]> = {
            draft: [],
            pending: [],
            active: [],
            expired: [],
            revoked: [],
            suspended: []
        };

        this.certificates.forEach(cert => {
            byStatus[cert.certificateStatus].push(cert);
        });

        return byStatus;
    }

    get activeCertificates() {
        return this.certificates.filter(cert => cert.certificateStatus === 'active');
    }

    get expiredCertificates() {
        return this.certificates.filter(cert => cert.certificateStatus === 'expired');
    }

    get pendingApplicationsCount() {
        return this.applications.filter(app =>
            ['submitted', 'under_review'].includes(app.applicationStatus)
        ).length;
    }

    /**
     * === CLEANUP ===
     */

    cleanup() {
        this.applications = [];
        this.pendingApplications = [];
        this.certificates = [];
        this.currentApplication = null;
        this.currentCertificate = null;
        this.currentTemplate = null;
        this.stats = null;
        this.verificationResult = null;
        this.resetForm();
        this.clearFilters();
        this.error = null;
    }


    /**
     * THÊM METHOD: Enhanced fetchUserCertificates với tab support
     * Sử dụng existing logic nhưng thêm tab filtering
     */
    async fetchUserCertificatesByType(
        certificateType: 'all' | CertificateType,
        options: {
            page?: number;
            pageSize?: number;
            status?: string;
            search?: string;
            sortBy?: string;
            sortOrder?: 'asc' | 'desc';
        } = {}
    ) {
        // Tái sử dụng existing method với enhanced filters
        const filters: CertificateSearchFilters = {};

        // Certificate type filter
        if (certificateType !== 'all') {
            filters.type = [certificateType];
        }

        // Status filter  
        if (options.status && options.status !== 'all') {
            filters.status = [options.status as CertificateStatus];
        }

        // Search filter
        if (options.search) {
            filters.keywords = options.search;
        }

        // Date range từ options nếu có
        if (options.sortBy === 'dateRange' && options.sortOrder) {
            // Có thể expand sau nếu cần
        }

        // SỬ DỤNG existing fetchUserCertificates method
        return await this.fetchUserCertificates({
            page: options.page,
            pageSize: options.pageSize,
            filters
        });
    }

    /**
     * THÊM METHOD: Set active tab và reload certificates
     * Không thay đổi existing logic
     */
    async setActiveTabAndReload(certificateType: 'all' | CertificateType) {
        this.activeTab = certificateType;

        // Reset pagination sử dụng existing properties
        this.certificatePage = 1;

        // Reload certificates using enhanced method
        return await this.fetchUserCertificatesByType(certificateType, {
            page: 1,
            pageSize: this.certificatePageSize || 20
        });
    }

    /**
     * THÊM METHOD: Enhanced search với debounce
     * Không conflict với existing search logic
     */
    searchCertificatesWithDebounce(searchTerm: string, certificateType: string = 'all') {
        // Clear previous timeout
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        // Set new timeout for debounced search
        this.searchTimeout = setTimeout(() => {
            this.fetchUserCertificatesByType(certificateType as any, {
                page: 1,
                pageSize: this.certificatePageSize || 20,
                search: searchTerm
            });
        }, 500);
    }

    /**
     * THÊM COMPUTED: Get certificates grouped by type
     * Sử dụng existing certificates array
     */
    get certificatesByTypeGrouped() {
        const certificates = this.certificates || [];

        return {
            all: certificates,
            vietrro: certificates.filter(c => c.certificateType === 'vietrro'),
            wipo: certificates.filter(c => c.certificateType === 'wipo'),
            noip: certificates.filter(c => c.certificateType === 'noip'),
            association: certificates.filter(c => c.certificateType === 'association')
        };
    }

    /**
     * THÊM COMPUTED: Statistics for each certificate type
     * Sử dụng existing certificates và existing computed logic pattern
     */
    get statisticsByType() {
        const certificates = this.certificates || [];

        const getStatsForType = (type: string) => {
            const typeCerts = type === 'all' ? certificates : certificates.filter(c => c.certificateType === type);

            return {
                total: typeCerts.length,
                active: typeCerts.filter(c => c.certificateStatus === 'active').length,
                pending: typeCerts.filter(c => c.certificateStatus === 'pending').length,
                expired: typeCerts.filter(c => c.certificateStatus === 'expired').length,
                revoked: typeCerts.filter(c => c.certificateStatus === 'revoked').length,
                suspended: typeCerts.filter(c => c.certificateStatus === 'suspended').length
            };
        };

        return {
            all: getStatsForType('all'),
            vietrro: getStatsForType('vietrro'),
            wipo: getStatsForType('wipo'),
            noip: getStatsForType('noip'),
            association: getStatsForType('association')
        };
    }

    /**
     * THÊM METHOD: Helper to get work title from certificate
     * Không conflict với existing helpers
     */
    getWorkTitleFromCertificate(certificate: Certificate): string {
        // Ưu tiên metadata.workTitle (từ applicationData)
        if (certificate.metadata?.workTitle) {
            return certificate.metadata.workTitle;
        }

        // Fallback sang content.title
        if (certificate.content?.title) {
            return certificate.content.title;
        }

        // Final fallback sang certificateNumber
        return certificate.certificateNumber;
    }

    /**
     * THÊM METHOD: Helper to get creators from certificate
     * Sử dụng existing data structure
     */
    getCreatorsFromCertificate(certificate: Certificate): Array<{ name: string; role: string; percentage: number }> {
        // Ưu tiên metadata.creators (từ applicationData.creators)
        if (certificate.metadata?.creators && Array.isArray(certificate.metadata.creators)) {
            return certificate.metadata.creators;
        }

        // Fallback tạo creator từ applicant (existing field)
        if (certificate.applicant) {
            const name = `${certificate.applicant.firstName || ''} ${certificate.applicant.lastName || ''}`.trim()
                || certificate.applicant.username;
            return [{
                name,
                role: 'Chủ sở hữu',
                percentage: 100
            }];
        }

        return [];
    }

    /**
     * THÊM METHOD: Client-side search trong existing certificates
     * Không replace existing search logic
     */
    searchCertificatesLocal(searchTerm: string): Certificate[] {
        if (!searchTerm.trim()) return this.certificates;

        const term = searchTerm.toLowerCase();
        return this.certificates.filter(cert => {
            const workTitle = this.getWorkTitleFromCertificate(cert).toLowerCase();
            const certNumber = cert.certificateNumber.toLowerCase();
            const description = cert.content?.description?.toLowerCase() || '';

            return workTitle.includes(term) ||
                certNumber.includes(term) ||
                description.includes(term);
        });
    }

    /**
     * THÊM METHOD: Filter certificates by status
     * Sử dụng existing certificatesByStatus pattern
     */
    filterCertificatesByStatusAndType(status?: CertificateStatus, type?: CertificateType): Certificate[] {
        let filtered = [...this.certificates];

        if (status) {
            filtered = filtered.filter(cert => cert.certificateStatus === status);
        }

        if (type) {
            filtered = filtered.filter(cert => cert.certificateType === type);
        }

        return filtered;
    }

    /**
     * THÊM COMPUTED: Enhanced version của existing computed properties
     * Thêm expiring soon logic
     */
    get certificatesExpiringSoon(): Certificate[] {
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

        return this.certificates.filter(cert => {
            if (!cert.expiryDate) return false;

            const expiryDate = new Date(cert.expiryDate);
            const now = new Date();

            return expiryDate <= thirtyDaysFromNow && expiryDate > now;
        });
    }

    /**
     * THÊM COMPUTED: Certificates cần attention
     * Expand existing logic patterns
     */
    get certificatesNeedingAttention(): Certificate[] {
        return this.certificates.filter(cert =>
            cert.certificateStatus === 'pending' ||
            cert.certificateStatus === 'suspended' ||
            this.certificatesExpiringSoon.includes(cert)
        );
    }

    /**
     * THÊM METHOD: Enhanced download với existing downloadCertificate logic
     * Không thay thế existing method
     */
    async downloadCertificateEnhanced(certificate: Certificate): Promise<boolean> {
        try {
            // Sử dụng existing certificateService.downloadCertificate
            const blob = await certificateService.downloadCertificate(certificate?.certificateFileUrl || certificate?.certificateFile);

            // Enhanced: Tạo filename từ work title
            const workTitle = this.getWorkTitleFromCertificate(certificate);
            const filename = `${generateSlug(workTitle)}-${certificate.certificateNumber}.pdf`
                .replace(/[^a-zA-Z0-9\-._]/g, '_'); // Sanitize filename

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);

            return true;
        } catch (error: any) {
            // Sử dụng existing error handling pattern
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể tải xuống chứng nhận';
            });
            throw error;
        }
    }

    /**
     * THÊM METHOD: Bulk operations
     * Sử dụng existing error handling patterns
     */
    async bulkDownloadCertificatesEnhanced(certificateIds: number[]): Promise<{ successful: number; failed: number }> {
        this.isLoading = true;
        this.error = null;

        try {
            const downloadPromises = certificateIds.map(id => {
                const cert = this.certificates.find(c => c.id === id);
                if (cert) {
                    return this.downloadCertificateEnhanced(cert);
                }
                return Promise.reject(new Error(`Certificate ${id} not found`));
            });

            const results = await Promise.allSettled(downloadPromises);

            const successful = results.filter(r => r.status === 'fulfilled').length;
            const failed = results.filter(r => r.status === 'rejected').length;

            runInAction(() => {
                this.isLoading = false;
            });

            return { successful, failed };
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể tải xuống hàng loạt';
                this.isLoading = false;
            });
            throw error;
        }
    }

    /**
     * THÊM METHOD: Enhanced cleanup
     * Extend existing cleanup không replace
     */
    cleanupEnhanced() {
        // Gọi existing cleanup nếu có
        if (typeof this.cleanup === 'function') {
            this.cleanup();
        }

        // Cleanup enhanced properties
        this.activeTab = 'all';
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = null;
        }
    }

    /**
     * THÊM METHOD: Validation helpers tương thích với existing validation pattern
     */
    validateCertificateForRenewal(certificate: Certificate): ValidationResult {
        const errors: Record<string, string> = {};

        if (!certificate) {
            errors.certificate = 'Không tìm thấy chứng nhận';
            return { isValid: false, errors };
        }

        if (certificate.certificateStatus !== 'expired') {
            errors.status = 'Chỉ có thể gia hạn chứng nhận đã hết hạn';
        }

        if (!certificate.content?.id) {
            errors.content = 'Không tìm thấy tác phẩm liên quan';
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }

    validateCertificateForRevocation(certificate: Certificate): ValidationResult {
        const errors: Record<string, string> = {};

        if (!certificate) {
            errors.certificate = 'Không tìm thấy chứng nhận';
            return { isValid: false, errors };
        }

        if (certificate.certificateStatus !== 'active') {
            errors.status = 'Chỉ có thể thu hồi chứng nhận đang hoạt động';
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }

    /**
 * Submit supplementary documents for certificate application
 * Used when application status is "additional_info_required"
 * 
 * @param documentId - Certificate application documentId
 * @param data - Files and optional notes
 /**
 * Submit supplementary documents for application
 */
    async submitSupplementaryDocuments(
        documentId: string,
        data: { files: File[]; notes?: string }
    ): Promise<void> {
        try {
            // Validate
            if (!data.files || data.files.length === 0) {
                throw new Error('Vui lòng chọn ít nhất một tài liệu');
            }

            // Validate file sizes (max 10MB per file)
            const MAX_SIZE = 10 * 1024 * 1024;
            for (const file of data.files) {
                if (file.size > MAX_SIZE) {
                    throw new Error(`File "${file.name}" vượt quá 10MB`);
                }
            }

            this.setSubmitting(true);
            this.error = null;

            // ✅ Gọi certificateService như các method khác
            const application = await certificateService.submitSupplementaryDocuments(
                documentId,
                data.files,
                data.notes
            );

            runInAction(() => {
                // Update current application
                this.currentApplication = application;

                // Update in applications list
                const index = this.applications.findIndex(
                    app => app.documentId === documentId
                );
                if (index !== -1) {
                    this.applications[index] = application;
                }

                this.setSubmitting(false);
            });

            // Refresh applications list
            await this.fetchUserApplications({});

        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message
                    || error.message
                    || 'Không thể gửi tài liệu bổ sung';
                this.setSubmitting(false);
            });
            throw error;
        }
    }

    /**
     * Get supplementary documents for application
     */
    async getSupplementaryDocuments(documentId: string): Promise<any[]> {
        try {
            this.setLoading(true);
            this.error = null;

            // ✅ Gọi certificateService
            const documents = await certificateService.getSupplementaryDocuments(documentId);

            runInAction(() => {
                this.setLoading(false);
            });

            return documents;

        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message
                    || error.message
                    || 'Không thể lấy danh sách tài liệu';
                this.setLoading(false);
            });
            throw error;
        }
    }
    /**
 * Delete a supplementary document
 */
    async deleteSupplementaryDocument(
        documentId: string,
        fileId: number
    ): Promise<void> {
        try {
            this.setSubmitting(true);
            this.error = null;

            const application = await certificateService.deleteSupplementaryDocument(
                documentId,
                fileId
            );

            runInAction(() => {
                // Update current application
                this.currentApplication = application;

                // Update in applications list
                const index = this.applications.findIndex(
                    app => app.documentId === documentId
                );
                if (index !== -1) {
                    this.applications[index] = application;
                }

                this.setSubmitting(false);
            });

            // Refresh applications
            await this.fetchUserApplications({});

        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message
                    || error.message
                    || 'Không thể xóa tài liệu';
                this.setSubmitting(false);
            });
            throw error;
        }
    }

    /**
     * Update supplementary notes
     */
    async updateSupplementaryNotes(
        documentId: string,
        notes: string
    ): Promise<void> {
        try {
            if (!notes || notes.trim().length === 0) {
                throw new Error('Ghi chú không được để trống');
            }

            this.setSubmitting(true);
            this.error = null;

            const application = await certificateService.updateSupplementaryNotes(
                documentId,
                notes
            );

            runInAction(() => {
                this.currentApplication = application;

                const index = this.applications.findIndex(
                    app => app.documentId === documentId
                );
                if (index !== -1) {
                    this.applications[index] = application;
                }

                this.setSubmitting(false);
            });

            await this.fetchUserApplications({});

        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message
                    || error.message
                    || 'Không thể cập nhật ghi chú';
                this.setSubmitting(false);
            });
            throw error;
        }
    }

    /**
     * ✅ NEW: Upload supporting documents (giấy phép để xem xét)
     * Uses certificateService
     * @param files - Array of files to upload
     * @returns Array of uploaded file IDs
     */
    async uploadSupportingDocuments(files: File[]): Promise<number[]> {
        try {
            this.setSubmitting(true);
            this.error = null;

            console.log('📤 Store: Uploading supporting documents...', {
                fileCount: files.length,
                files: files.map(f => ({ name: f.name, size: f.size }))
            });

            // ✅ Call service layer - NOT direct fetch!
            const fileIds = await certificateService.uploadSupportingDocuments(files);

            console.log('✅ Store: Supporting documents uploaded:', fileIds);

            runInAction(() => {
                this.setSubmitting(false);
            });

            return fileIds;
        } catch (error: any) {
            console.error('❌ Store: Upload error:', error);

            runInAction(() => {
                this.error = error.message;
                this.setSubmitting(false);
            });

            throw error;
        }
    }

    /**
     * ✅ NEW: Upload certificate file (file chứng nhận cuối cùng)
     * For NOIP/Association certificates only
     * Uses certificateService
     * @param file - Certificate PDF file
     * @returns Uploaded file ID
     */
    async uploadCertificateFile(file: File): Promise<number> {
        try {
            this.setSubmitting(true);
            this.error = null;

            console.log('📤 Store: Uploading certificate file...', {
                name: file.name,
                size: (file.size / 1024 / 1024).toFixed(2) + ' MB'
            });

            // ✅ Call service layer - NOT direct fetch!
            const fileId = await certificateService.uploadCertificateFile(file);

            console.log('✅ Store: Certificate file uploaded:', fileId);

            runInAction(() => {
                this.setSubmitting(false);
            });

            return fileId;
        } catch (error: any) {
            console.error('❌ Store: Certificate file upload error:', error);

            runInAction(() => {
                this.error = error.message;
                this.setSubmitting(false);
            });

            throw error;
        }
    }
}

export default CertificateStore;
